import i18n from "i18n";

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
const INDIAN_PHONE_REGEX = /^(?:\+91|0)?[6-9]\d{9}$/;
const COMBINED_LOGIN_REGEX =
  /^([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}|[0-9]{10})?$/;

export { EMAIL_REGEX, INDIAN_PHONE_REGEX, COMBINED_LOGIN_REGEX };

export function getCustomerLoginMode(customerLoginMethod) {
  const normalizedMethod = String(customerLoginMethod || "").toLowerCase();

  if (normalizedMethod === "phone" || normalizedMethod === "email") {
    return normalizedMethod;
  }

  return "combined";
}

export function getLoginFieldContent(loginMode) {
  const contentByMode = {
    phone: {
      label: i18n.t("login.field.phone.label"),
      placeholder: i18n.t("login.field.phone.placeholder"),
      helperText: i18n.t("login.field.phone.helperText"),
      patternErrorText: i18n.t("login.field.phone.patternErrorText"),
      requiredErrorText: i18n.t("login.field.phone.requiredErrorText"),
      pattern: INDIAN_PHONE_REGEX,
    },
    email: {
      label: i18n.t("login.field.email.label"),
      placeholder: i18n.t("login.field.email.placeholder"),
      helperText: i18n.t("login.field.email.helperText"),
      patternErrorText: i18n.t("login.field.email.patternErrorText"),
      requiredErrorText: i18n.t("login.field.email.requiredErrorText"),
      pattern: EMAIL_REGEX,
    },
    combined: {
      light: {
        label: i18n.t("login.field.combinedLight.label"),
        placeholder: i18n.t("login.field.combinedLight.placeholder"),
        helperText: i18n.t("login.field.combinedLight.helperText"),
        patternErrorText: i18n.t("login.field.combinedLight.patternErrorText"),
        requiredErrorText: i18n.t("login.field.combinedLight.requiredErrorText"),
        pattern: COMBINED_LOGIN_REGEX,
      },
      dark: {
        label: i18n.t("login.field.combinedDark.label"),
        placeholder: i18n.t("login.field.combinedDark.placeholder"),
        helperText: i18n.t("login.field.combinedDark.helperText"),
        patternErrorText: i18n.t("login.field.combinedDark.patternErrorText"),
        requiredErrorText: i18n.t("login.field.combinedDark.requiredErrorText"),
        pattern: COMBINED_LOGIN_REGEX,
      },
    },
  };

  if (loginMode === "phone" || loginMode === "email") {
    return {
      light: contentByMode[loginMode],
      dark: contentByMode[loginMode],
    };
  }

  return contentByMode.combined;
}

export function normalizePhoneNumber(value) {
  const trimmedValue = String(value || "").replace(/\s/g, "");

  if (!trimmedValue) {
    return "";
  }

  const digitsOnly = trimmedValue.replace(/\D/g, "");

  if (!digitsOnly) {
    return "";
  }

  return digitsOnly.slice(-10);
}

export function normalizeEmail(value) {
  return String(value || "").replace(/\s/g, "").toLowerCase();
}

export function isValidPhoneNumber(value) {
  return INDIAN_PHONE_REGEX.test(String(value || "").replace(/\s/g, ""));
}

export function isValidEmail(value) {
  return EMAIL_REGEX.test(normalizeEmail(value));
}

export function getLoginPayload(value, loginMode) {
  const trimmedValue = String(value || "").replace(/\s/g, "");

  if (!trimmedValue) {
    return null;
  }

  if (loginMode === "phone") {
    const normalizedPhone = normalizePhoneNumber(trimmedValue);

    return {
      email: null,
      phone: normalizedPhone,
      inputValue: normalizedPhone,
      isPhoneLogin: true,
    };
  }

  if (loginMode === "email") {
    const normalizedEmail = normalizeEmail(trimmedValue);

    return {
      email: normalizedEmail,
      phone: null,
      inputValue: normalizedEmail,
      isPhoneLogin: false,
    };
  }

  const isPhoneLogin = /^\d+$/.test(trimmedValue);

  if (isPhoneLogin) {
    const normalizedPhone = normalizePhoneNumber(trimmedValue);

    return {
      email: null,
      phone: normalizedPhone,
      inputValue: normalizedPhone,
      isPhoneLogin: true,
    };
  }

  const normalizedEmail = normalizeEmail(trimmedValue);

  return {
    email: normalizedEmail,
    phone: null,
    inputValue: normalizedEmail,
    isPhoneLogin: false,
  };
}

export function resolvePrefilledLoginPayload({
  loginMode,
  userPhone,
  userEmail,
}) {
  if (loginMode === "phone") {
    if (userPhone && userPhone.trim() && isValidPhoneNumber(userPhone)) {
      return getLoginPayload(userPhone, "phone");
    }
    return null;
  }

  if (loginMode === "email") {
    if (userEmail && userEmail.trim() && isValidEmail(userEmail)) {
      return getLoginPayload(userEmail, "email");
    }
    return null;
  }

  if (userPhone && userPhone.trim()) {
    const normalizedPhone = normalizePhoneNumber(userPhone);

    if (normalizedPhone) {
      return {
        email: null,
        phone: normalizedPhone,
        inputValue: normalizedPhone,
        isPhoneLogin: true,
      };
    }
  }

  if (userEmail && userEmail.trim() && isValidEmail(userEmail)) {
    return getLoginPayload(userEmail, "combined");
  }

  return null;
}
